/**
 * The inventory tree (MeasurementPanel.uxml order, MeasurementPanelView.Sections.cs), the
 * default-off set and the shape filter classes. Row keys are the desktop MeasurementNames.
 */

export type Shape = 'MASK' | 'CURVE' | 'LINE' | 'POINT' | 'ANGLE' | 'PLANE';

export interface RowDef {
  key: string;
  title: string;
  shape: Shape;
}

export interface SectionDef {
  title: string;
  rows: RowDef[];
}

const r = (key: string, title: string, shape: Shape): RowDef => ({ key, title, shape });

export const SECTIONS: SectionDef[] = [
  {
    title: 'GEOMETRIC HEIGHTS',
    rows: [r('NGeometricHeight', 'N Geometric Height', 'CURVE'), r('RGeometricHeight', 'R Geometric Height', 'CURVE'), r('LGeometricHeight', 'L Geometric Height', 'CURVE')],
  },
  {
    title: 'FREE MARGINS',
    rows: [r('RnLnFreeMargin', 'RN–LN Free Margin', 'CURVE'), r('RnRlFreeMargin', 'RN–RL Free Margin', 'CURVE'), r('RlLnFreeMargin', 'RL–LN Free Margin', 'CURVE')],
  },
  {
    title: 'VIRTUAL BASAL RING',
    rows: [
      r('VbrEllipse', 'VBR Ellipse', 'CURVE'),
      r('VbrMaxDiameter', 'VBR Maximum Diameter', 'LINE'),
      r('VbrMinDiameter', 'VBR Minimum Diameter', 'LINE'),
      r('VbrPlane', 'VBR Plane', 'PLANE'),
      r('VbrArea', 'VBR Area', 'CURVE'),
    ],
  },
  {
    title: 'SINUS OF VALSALVA',
    rows: [
      r('SovContour', 'SOV Contour', 'CURVE'),
      r('SovHeight', 'SOV Height', 'LINE'),
      r('SovMaxDiameter', 'SOV Maximum Diameter', 'LINE'),
      r('SovMinDiameter', 'SOV Minimum Diameter', 'LINE'),
      r('SovArea', 'SOV Area', 'CURVE'),
    ],
  },
  {
    title: 'SINUTUBULAR JUNCTION',
    rows: [
      r('StjContour', 'STJ Contour', 'CURVE'),
      r('StjHeight', 'STJ Height', 'LINE'),
      r('StjMaxDiameter', 'STJ Maximum Diameter', 'LINE'),
      r('StjMinDiameter', 'STJ Minimum Diameter', 'LINE'),
      r('StjArea', 'STJ Area', 'CURVE'),
    ],
  },
  {
    title: 'ASCENDING AORTA',
    rows: [
      r('AscAortaContour', 'Ascending Aorta Contour', 'CURVE'),
      r('AscAortaMaxDiameter', 'Ascending Aorta Maximum Diameter', 'LINE'),
      r('AscAortaMinDiameter', 'Ascending Aorta Minimum Diameter', 'LINE'),
      r('AscAortaHeight', 'Ascending Aorta Height', 'LINE'),
    ],
  },
  {
    title: 'CORONARY OSTIA',
    rows: [r('LcaCoronaryOstia', 'LCA Coronary Ostium', 'POINT'), r('RcaCoronaryOstia', 'RCA Coronary Ostium', 'POINT'), r('LcaHeight', 'LCA Height', 'LINE'), r('RcaHeight', 'RCA Height', 'LINE')],
  },
  {
    title: 'COAPTATION',
    rows: [r('CentralCoaptationPoint', 'Root Centroid Point', 'POINT'), r('EffectiveHeight', 'Effective Height', 'LINE')],
  },
  {
    title: 'COMMISSURES',
    rows: [
      r('RlCommissure', 'RL Commissure', 'POINT'),
      r('RnCommissure', 'RN Commissure', 'POINT'),
      r('LnCommissure', 'LN Commissure', 'POINT'),
      r('RlHeight', 'RL Height', 'LINE'),
      r('RnHeight', 'RN Height', 'LINE'),
      r('LnHeight', 'LN Height', 'LINE'),
      r('RlRnLength', 'RL-RN Length', 'LINE'),
      r('RlLnLength', 'RL-LN Length', 'LINE'),
      r('RnLnLength', 'RN-LN Length', 'LINE'),
    ],
  },
  {
    title: 'COMMISSURAL ANGLES',
    rows: [r('CommissuralAnglePoint', 'Commissural Angle Point', 'POINT'), r('RAngle', 'R Angle', 'ANGLE'), r('LAngle', 'L Angle', 'ANGLE'), r('NAngle', 'N Angle', 'ANGLE')],
  },
  {
    title: 'CENTERLINE',
    rows: [r('Centerline', 'Root Centroid Line', 'CURVE'), r('AortaCurve', 'Centerline', 'CURVE')],
  },
  {
    title: 'NADIRS',
    rows: [r('LNadir', 'L Nadir', 'POINT'), r('NNadir', 'N Nadir', 'POINT'), r('RNadir', 'R Nadir', 'POINT')],
  },
  {
    title: 'SEGMENTATIONS',
    rows: [
      r('AorticRoot', 'Aortic Root', 'MASK'),
      r('LSinus', 'L Sinus', 'MASK'),
      r('NSinus', 'N Sinus', 'MASK'),
      r('RSinus', 'R Sinus', 'MASK'),
      r('LeafletNSegmentation', 'Leaflet N', 'MASK'),
      r('LeafletRSegmentation', 'Leaflet R', 'MASK'),
      r('LeafletLSegmentation', 'Leaflet L', 'MASK'),
      r('LeafletNModelSegmentation', 'Leaflet N (model)', 'MASK'),
      r('LeafletRModelSegmentation', 'Leaflet R (model)', 'MASK'),
      r('LeafletLModelSegmentation', 'Leaflet L (model)', 'MASK'),
      r('CalciumSegmentation', 'Calcium', 'MASK'),
      r('LVOT', 'LVOT', 'MASK'),
      r('AortaLvSegmentation', 'Aorta/LV', 'MASK'),
      r('CoronariesSegmentation', 'Coronaries', 'MASK'),
      r('LeftAtriumSegmentation', 'Left Atrium', 'MASK'),
      r('RightAtriumSegmentation', 'Right Atrium', 'MASK'),
      r('PulmonaryArterySegmentation', 'Pulmonary Artery', 'MASK'),
      r('MidAscendingAortaSegmentation', 'Mid Ascending Aorta', 'MASK'),
      r('DistalAscendingAortaSegmentation', 'Distal Ascending Aorta', 'MASK'),
      r('MidTransverseArchSegmentation', 'Mid Transverse Arch', 'MASK'),
      r('MidDescendingAortaSegmentation', 'Mid Descending Aorta', 'MASK'),
      r('VbrSegmentation', 'VBR', 'MASK'),
      r('SovSegmentation', 'SOV', 'MASK'),
      r('StjSegmentation', 'STJ', 'MASK'),
      r('OstiaSegmentation', 'Ostia', 'MASK'),
      r('CommissuresSegmentation', 'Commissures', 'MASK'),
      r('CoaptationSegmentation', 'Coaptation', 'MASK'),
      r('CoaptationDeficiencySegmentation', 'Coaptation Deficiency', 'MASK'),
    ],
  },
];

export const SEGMENTATION_KEYS = new Set(SECTIONS.find((s) => s.title === 'SEGMENTATIONS')!.rows.map((x) => x.key));

/** DefaultOffMeasurementNames. */
export const DEFAULT_OFF = new Set<string>([
  'Centerline',
  'AortaCurve',
  ...SEGMENTATION_KEYS,
  'VbrPlane',
  'VbrMaxDiameter',
  'VbrMinDiameter',
  'SovMaxDiameter',
  'SovMinDiameter',
  'StjMaxDiameter',
  'StjMinDiameter',
  'AscAortaMaxDiameter',
  'AscAortaMinDiameter',
  'SovHeight',
  'StjHeight',
  'AscAortaHeight',
  'EffectiveHeight',
  'CentralCoaptationPoint',
  'AscAortaContour',
  'RlRnLength',
  'RlLnLength',
  'RnLnLength',
]);

/** InventoryVisibilityStorage.ProductDefaultOnKeys. */
export const PRODUCT_DEFAULT_ON = new Set<string>([
  'NGeometricHeight',
  'RGeometricHeight',
  'LGeometricHeight',
  'RnLnFreeMargin',
  'RnRlFreeMargin',
  'RlLnFreeMargin',
  'LNadir',
  'NNadir',
  'RNadir',
  'VbrEllipse',
  'VbrArea',
  'SovContour',
  'SovArea',
  'StjContour',
  'StjArea',
  'LcaCoronaryOstia',
  'RcaCoronaryOstia',
  'LcaHeight',
  'RcaHeight',
  'RlCommissure',
  'RnCommissure',
  'LnCommissure',
  'RlHeight',
  'RnHeight',
  'LnHeight',
  'CommissuralAnglePoint',
  'RAngle',
  'LAngle',
  'NAngle',
]);

export function isDefaultVisible(key: string): boolean {
  return !DEFAULT_OFF.has(key);
}

/** inventory_visibility.json (schema 6): sparse overrides; the read rules of
 * InventoryVisibilityStorage. Returns key -> visible. */
export function readInventoryVisibility(json: unknown): Map<string, boolean> {
  const out = new Map<string, boolean>();
  if (!json || typeof json !== 'object') return out;
  const obj = json as { schema_version?: number; visibility?: Record<string, unknown> };
  const version = typeof obj.schema_version === 'number' ? obj.schema_version : 1;
  const vis = obj.visibility ?? {};
  for (const [key, value] of Object.entries(vis)) {
    if (typeof value !== 'boolean') continue;
    if (version < 2 && value && /^(Vbr|Sov|Stj)(Max|Min)Diameter$/.test(key)) continue;
    if (version < 4 && value && key === 'CentralCoaptationPoint') continue;
    if (version < 6 && value && key === 'AscAortaContour') continue;
    if (!value && PRODUCT_DEFAULT_ON.has(key)) continue;
    if (value && SEGMENTATION_KEYS.has(key)) continue;
    out.set(key, value);
  }
  return out;
}
